import React from "react";
import {
  calculateVoiceConfidence,
  confidenceLabel,
  isCloneDnaOutdated,
} from "./cloneUtils";
import VoiceSignature from "./VoiceSignature";

// =============================================================================
// VOICE SIGNATURE DERIVATION
//
// Every trained twin gets a unique 6-axis "voice fingerprint" shape built from
// the SAME six dimensions — intuitive, plain-language traits a layperson reads
// instantly (long vs short, casual vs formal, simple vs complex…). Crucially
// each one is derived from REAL distilled Voice-DNA numbers (no calibration
// needed, nothing invented), so every card is directly comparable: only the
// SHAPE differs between twins, never the labels.
// =============================================================================

// Order MUST match the values array in deriveVoiceMetrics. High value = "more
// of this word": longer / more formal / more complex / richer / more niche /
// more characterful.
const VOICE_AXES = [
  { label: "Length" },       // avg email length
  { label: "Formality" },    // casual ↔ formal register
  { label: "Complexity" },   // simple ↔ complex sentences
  { label: "Dialect" },      // how distinctive / "own" the word choice is
  { label: "Specific" },     // broad ↔ narrow, well-defined subject world
  { label: "Personality" },  // plain ↔ characterful (quirks, humour, markers)
];

const clamp01 = (n) => Math.max(0, Math.min(1, n));

// Fallback register read when the LLM didn't stamp a registerScale.
const TONE_REGISTER = { casual: 0.22, mixed: 0.5, formal: 0.8 };

// Map the distilled Voice DNA → six 0..1 trait strengths. Every input is a
// real DNA number; thresholds are calibrated against the ranges real email
// voices fall into so the shapes stay expressive.
const deriveVoiceMetrics = (clone) => {
  const dna = clone.voiceDNA || {};
  const sp = dna.structureProfile || {};
  const rh = dna.rhythm || {};
  const wd = dna.wordDna || {};
  const ch = dna.character || {};
  const vc = dna.voiceContext || {};

  // 1. Length — avg words per email (~40 short … ~240 long-form)
  const avgWords = Number(sp.avgWordsPerEmail) || 0;
  const length = clamp01((avgWords - 40) / 200);

  // 2. Formality — register 1..10 (street → boardroom); tone hint as fallback
  const reg = Number(dna.vocabulary?.registerScale);
  const formality =
    Number.isFinite(reg) && reg > 0
      ? clamp01((reg - 1) / 9)
      : TONE_REGISTER[clone.identity?.tone] ?? 0.5;

  // 3. Complexity — avg sentence length (~8 punchy … ~26 dense)
  const asw =
    Number(rh.avgSentenceWords) ||
    (sp.avgWordsPerEmail && sp.avgSentencesPerEmail
      ? sp.avgWordsPerEmail / sp.avgSentencesPerEmail
      : 0);
  const complexity = clamp01((asw - 8) / 18);

  // 4. Wording — how distinctive the word choice is (the writer's "own" words
  //    lead; everyday words add a little)
  const special = Array.isArray(wd.specialWords) ? wd.specialWords.length : 0;
  const base = Array.isArray(wd.baseWords) ? wd.baseWords.length : 0;
  const wording = clamp01((special * 1.1 + base * 0.4) / 16);

  // 5. Specific — how defined / niche the subject world is (vs general-purpose):
  //    a clear expertise + concrete recurring themes read as a narrow focus.
  const topicCount = Array.isArray(vc.topics) ? vc.topics.length : 0;
  const specific = clamp01(
    (vc.expertise ? 0.45 : 0) +
      (vc.persona ? 0.15 : 0) +
      (vc.offering ? 0.1 : 0) +
      clamp01(topicCount / 4) * 0.45,
  );

  // 6. Personality — recurring personal markers + quirks + humour
  const markers =
    (ch.greeting?.word ? 1 : 0) +
    (ch.signOff?.word ? 1 : 0) +
    (Array.isArray(ch.catchphrases) ? ch.catchphrases.length : 0);
  const quirks = Array.isArray(dna.quirks) ? dna.quirks.length : 0;
  const humour =
    dna.humorType && String(dna.humorType).toLowerCase() !== "none" ? 1 : 0;
  const personality = clamp01((markers + quirks + humour) / 7);

  return [length, formality, complexity, wording, specific, personality];
};

// Returns { values:[6]|null }. values=null ⇒ dormant (untrained) lattice.
const deriveVoiceSignature = (clone) => {
  if (!clone.voiceDNA) return { values: null };
  return { values: deriveVoiceMetrics(clone) };
};

// --- sub-components -----------------------------------------------------------

// Default twin avatar — a minimal voice waveform mark. Monochrome, inherits
// the brand-blue via currentColor, with a subtle taper so it reads as a
// living voice rather than a generic equalizer.
const VoiceMark = () => (
  <svg
    className="cca-card-avatar-mark"
    viewBox="0 0 24 24"
    aria-hidden="true"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
  >
    <line x1="4" y1="10" x2="4" y2="14" opacity="0.55" />
    <line x1="8" y1="7" x2="8" y2="17" opacity="0.8" />
    <line x1="12" y1="3.5" x2="12" y2="20.5" />
    <line x1="16" y1="6" x2="16" y2="18" opacity="0.8" />
    <line x1="20" y1="9" x2="20" y2="15" opacity="0.55" />
  </svg>
);

const ConfidenceBar = ({ score }) => {
  const meta = confidenceLabel(score);
  return (
    <div className="cca-card-confidence">
      <div className="cca-card-confidence-meta">
        <span className="cca-card-confidence-label" style={{ color: meta.color }}>
          {meta.label}
        </span>
        <span className="cca-card-confidence-score">{score}%</span>
      </div>
      <div className="cca-card-confidence-track">
        <div
          className="cca-card-confidence-fill"
          style={{ width: `${score}%`, background: meta.color }}
        />
      </div>
    </div>
  );
};

const Stat = ({ value, label }) => (
  <span className="cca-card-stat">
    <span className="cca-card-stat-num">{value}</span>
    <span className="cca-card-stat-label">{label}</span>
  </span>
);

const CloneCard = ({ clone, onOpen, onDelete }) => {
  const score = calculateVoiceConfidence(clone);
  const sampleCount =
    (clone.voiceSamples?.fullEmails?.length || 0) +
    (clone.voiceSamples?.subjectLines?.length || 0);
  const sentCount = clone.stats?.totalEmailsGenerated || 0;
  const templatesUsing = clone.stats?.templatesUsing || 0;
  const isTrained = !!clone.voiceDNA;
  const dnaOutdated = isCloneDnaOutdated(clone);

  const sig = deriveVoiceSignature(clone);
  const subtitle = isTrained
    ? "Trained voice profile"
    : "Awaiting your voice — add samples to train";

  return (
    <button
      type="button"
      className={`cca-card ${isTrained ? "is-trained" : "is-draft"}`}
      onClick={() => onOpen(clone.id)}
    >
      <span className="cca-card-shine" aria-hidden="true" />

      <div className="cca-card-head">
        <div className="cca-card-avatar-wrap">
          <span className="cca-card-avatar-halo" aria-hidden="true" />
          <div className="cca-card-avatar">
            {clone.avatar ? <span>{clone.avatar}</span> : <VoiceMark />}
          </div>
          <span
            className="cca-card-status-dot"
            data-status={clone.status || "draft"}
            title={clone.status || "draft"}
          />
        </div>

        <div className="cca-card-identity">
          <div className="cca-card-name">{clone.name || "Unnamed CopyTwin"}</div>
          <div className="cca-card-subtitle">{subtitle}</div>
        </div>

        <span
          className={`cca-card-trained-pill ${
            isTrained ? "is-trained" : "is-draft"
          }`}
          title={isTrained ? "CopyTwin trained" : "Not yet trained"}
        >
          <i
            className={`fa-solid ${
              isTrained ? "fa-circle-check" : "fa-circle-dashed"
            }`}
          />
          {isTrained ? "Trained" : "Draft"}
        </span>
      </div>

      {dnaOutdated && (
        <div
          className="cca-card-update-banner"
          title="Re-train to unlock the latest style engine"
        >
          <i className="fa-solid fa-arrow-up-right-dots" />
          <span>Re-train for the latest voice engine</span>
        </div>
      )}

      <div className="cca-card-signature">
        <VoiceSignature values={sig.values} axes={VOICE_AXES} showLabels={isTrained} />
        {!isTrained && (
          <span className="cca-card-signature-hint">Voice signature forms after training</span>
        )}
      </div>

      <ConfidenceBar score={score} />

      <div className="cca-card-stats">
        <Stat value={sampleCount} label="samples" />
        <Stat value={sentCount} label="sent" />
        {isTrained && <Stat value={templatesUsing} label="templates" />}
      </div>

      <div
        className="cca-card-delete"
        role="button"
        tabIndex={0}
        onClick={(e) => {
          e.stopPropagation();
          onDelete(clone);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.stopPropagation();
            onDelete(clone);
          }
        }}
        title="Delete CopyTwin"
      >
        <i className="fa-regular fa-trash-can" />
      </div>
    </button>
  );
};

const NewCloneCard = ({ onCreate }) => (
  <button type="button" className="cca-card cca-card-new" onClick={onCreate}>
    <span className="cca-card-new-glow" aria-hidden="true" />
    <div className="cca-card-new-icon">
      <span className="cca-card-new-icon-ring" aria-hidden="true" />
      <i className="fa-solid fa-plus" />
    </div>
    <div className="cca-card-new-title">New CopyTwin</div>
    <div className="cca-card-new-hint">
      Train a copywriter twin that writes exactly like you
    </div>
    <span className="cca-card-new-cta">
      <i className="fa-solid fa-arrow-right" />
      Create twin
    </span>
  </button>
);

const EmptyState = ({ onCreate }) => (
  <div className="cca-empty-stage">
    <div className="cca-orbs" aria-hidden="true">
      <span className="cca-orb cca-orb-1" />
      <span className="cca-orb cca-orb-2" />
      <span className="cca-orb cca-orb-3" />
      <span className="cca-orb cca-orb-4" />
      <span className="cca-orb cca-orb-5" />
      <span className="cca-orb cca-orb-6" />
      <span className="cca-orb-grid" />
    </div>

    <div className="cca-empty">
      <div className="cca-empty-icon-wrap">
        <div className="cca-empty-icon-ring cca-empty-icon-ring-1" />
        <div className="cca-empty-icon-ring cca-empty-icon-ring-2" />
        <div className="cca-empty-icon">
          <i className="fa-solid fa-wand-magic-sparkles" />
        </div>
      </div>

      <h2 className="cca-empty-title">Build your first CopyTwin</h2>
      <p className="cca-empty-subtitle">
        Train your AI copywriter twin on your real writing: voice, rhythm, signature phrases.
        <br />
        Then drop it into any email template for instant on-brand copy.
      </p>

      <button type="button" className="cca-empty-cta" onClick={onCreate}>
        <span className="cca-empty-cta-label">Create your CopyTwin</span>
        <span className="cca-empty-cta-icon">
          <i className="fa-solid fa-arrow-right" />
        </span>
        <span className="cca-empty-cta-shine" />
      </button>
    </div>
  </div>
);

const CloneListView = ({ clones, onOpen, onCreate, onDelete }) => {
  if (!clones.length) {
    return <EmptyState onCreate={onCreate} />;
  }

  return (
    <div className="cca-list">
      <div className="cca-list-header">
        <div className="cca-list-headline">
          <div className="cca-list-eyebrow">
            <i className="fa-solid fa-fingerprint" />
            <span>Voice library</span>
            <span className="cca-list-eyebrow-count">
              {clones.length} {clones.length === 1 ? "twin" : "twins"}
            </span>
          </div>
          <h2 className="cca-list-title">CopyTwin AI</h2>
          <p className="cca-list-subtitle">
            Your reusable AI copywriter twins. Pick one in any email template.
          </p>
        </div>
        <button type="button" className="cca-list-new-btn" onClick={onCreate}>
          <i className="fa-solid fa-plus" />
          New CopyTwin
        </button>
      </div>

      <div className="cca-grid">
        {clones.map((c) => (
          <CloneCard
            key={c.id}
            clone={c}
            onOpen={onOpen}
            onDelete={onDelete}
          />
        ))}
        <NewCloneCard onCreate={onCreate} />
      </div>
    </div>
  );
};

export default CloneListView;
